import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import { useTheme } from '@mui/material';

import useSmartLoading from '../../hooks/useSmartLoading';

const CrossFade = ({
  showSkeleton: isLoading,
  skeleton,
  wrapperProps,
  children,
  graceMs = 0,
  minDisplayMs = 0,
}) => {
  const smartLoading = useSmartLoading(isLoading, { graceMs, minDisplayMs });
  const [showSkeleton, setShowSkeleton] = useState(smartLoading);
  const theme = useTheme();

  useEffect(() => {
    if (smartLoading) {
      setShowSkeleton(true);
    } else {
      // Delay hiding the skeleton so the Fade-out animation can play
      const timer = setTimeout(
        () => setShowSkeleton(false),
        theme.transitions.duration.enteringScreen,
      );
      return () => clearTimeout(timer);
    }
  }, [smartLoading, theme?.transitions?.duration?.enteringScreen]);

  return (
    <Box
      id={'cross-fade'}
      {...wrapperProps}
      sx={{
        width: '100%', // Fill the width of parent
        display: 'block', // Make the container fit content
        position: 'relative', // Relative position for stacking
        overflow: 'hidden', // Hide overflow for skeleton
        ...wrapperProps?.sx,
      }}
    >
      {skeleton && (
        // appear={false}: when the skeleton is already meant to be visible on
        // mount (mid-load, seeded by useSmartLoading), mount it opaque instead of
        // fading up from 0 — that fade left a blank frame right after the spinner
        // unmounted. Later content→skeleton reloads are `enter` transitions
        // (unaffected by `appear`), so those still animate.
        <Fade in={showSkeleton} appear={false}>
          <Box
            sx={{
              height: '100%', // Fill the height of parent
              minHeight: children ? '0' : 'auto', // Allow it to collapse when there are no children
              position: 'absolute', // Position skeleton absolutely
              top: '0', // Stack it on top
              left: '0',
              right: '0',
              bottom: '0',
            }}
          >
            {skeleton}
          </Box>
        </Fade>
      )}
      <Fade in={!smartLoading && !showSkeleton}>
        <Box
          sx={{
            height: 'auto', // Allow children to define height
            position: 'relative', // Position relative to parent
            zIndex: 1, // Ensure children are above skeleton
          }}
        >
          {children}
        </Box>
      </Fade>
    </Box>
  );
};

export { CrossFade };
